<template>
  <div class="nested">
    <draggable
      tag="div"
      :class="[{ isEmpty: pages.length === 0 }, 'item-container']"
      :item-key="(el: PagesForestNode) => el.id"
      :list="pages"
      v-bind="dragOptions"
      @change="handleChange"
    >
      <template #item="{ element }">
        <div class="item-group" :class="{ hidden: !displayChildren }">
          <PagesListItem
            :writable="writable"
            :collapsed-folders="collapsedFolders"
            :page="element"
            :selected-page-id="selectedPageId"
            @toggle-folder="(pl) => $emit('toggleFolder', pl)"
            @open-page="(id, kind, event) => $emit('openPage', id, kind, event)"
            @select-page="
              (id, kind, event) => $emit('selectPage', id, kind, event)
            "
            @show-context-menu="
              (id, kind, event) => $emit('showContextMenu', id, kind, event)
            "
            @add-page-to="(pl) => $emit('addPageTo', pl)"
          />
          <PagesList
            class="item-sub"
            :display-children="!collapsedFolders.includes(element.id)"
            :writable="writable"
            :collapsed-folders="collapsedFolders"
            :pages="element.children"
            :opened-page-id="element.id"
            :selected-page-id="selectedPageId"
            @add-page-to="(payload) => $emit('addPageTo', payload)"
            @open-page="(id, kind, event) => $emit('openPage', id, kind, event)"
            @move-page="
              (id, parentId, index) => $emit('movePage', id, parentId, index)
            "
            @reorder-page="
              (id, parentId, index) => $emit('reorderPage', id, parentId, index)
            "
            @reset-open-folders="() => $emit('resetOpenFolders')"
            @select-page="
              (id, kind, event) => $emit('selectPage', id, kind, event)
            "
            @show-context-menu="
              (id, kind, event) => $emit('showContextMenu', id, kind, event)
            "
            @toggle-folder="(payload) => $emit('toggleFolder', payload)"
          />
        </div>
      </template>
    </draggable>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
// Importing the package directly breaks the build in vite+rollup prod build
import draggable from "vuedraggable/src/vuedraggable";

import PagesListItem from "@/components/pages/PagesListItem.vue";
import { type PagesForestNode } from "@/page";
import { mountSortableCancelOnEscape } from "@/utils/sortable";

type ChangeEvent =
  | {
      added: {
        newIndex: number;
        element: PagesForestNode;
      };
    }
  | {
      removed: {
        oldIndex: number;
        element: PagesForestNode;
      };
    }
  | {
      moved: {
        newIndex: number;
        oldIndex: number;
        element: PagesForestNode;
      };
    };

export default defineComponent({
  name: "PagesList",
  components: {
    draggable,
    PagesListItem,
  },
  props: {
    displayChildren: { type: Boolean, required: true },
    // Whether the rows offer their hover "+" (subpage creation) and can be
    // dragged (re-parenting writes) — see PagesForestPane.
    writable: { type: Boolean, default: true },
    openedPageId: {
      type: [String, null] as PropType<string | null>,
      required: true,
    },
    collapsedFolders: { type: Array as PropType<string[]>, required: true },
    pages: {
      required: true,
      type: Array as PropType<Array<PagesForestNode>>,
    },
    selectedPageId: {
      type: [String, null] as PropType<string | null>,
      required: true,
    },
  },
  emits: {
    addPageTo: (_id: string) => true,
    openAllFolders: () => true,
    openPage: (
      _id: string,
      _kind: PagesForestNode["kind"],
      _event?: MouseEvent
    ) => true,
    // A drag dropped the page under a new parent (null = the root) at the
    // given sibling index: the pane owner records the order and persists
    // the move — this component only reports the intent; it holds no
    // controller access (the same tree serves data models with different
    // persistence, see `PagesForestSource`).
    movePage: (_id: string, _parentId: string | null, _newIndex: number) =>
      true,
    // A drag reordered the page among its current siblings (parent
    // unchanged, so nothing to persist — sibling order is device-local).
    reorderPage: (_id: string, _parentId: string | null, _newIndex: number) =>
      true,
    resetOpenFolders: () => true,
    selectPage: (
      _id: string,
      _kind: PagesForestNode["kind"],
      _event?: MouseEvent
    ) => true,
    showContextMenu: (
      _id: string,
      _kind: PagesForestNode["kind"],
      _event: MouseEvent
    ) => true,
    toggleFolder: (_id: string) => true,
  },
  setup(props, { emit }) {
    mountSortableCancelOnEscape();

    const dragOptions = computed(() => ({
      animation: 100,
      ghostClass: "ghost",
      dragClass: "dragging",
      handle: ".handle",
      group: { name: "pages" },
      // Re-parenting and reordering write: both are disabled when the pages
      // cannot be written (read-only workspace member).
      disabled: !props.writable,
      // The whole row is the drag handle, so without this a mousedown on
      // the collapse chevron (or the + button) arms a drag and the
      // slightest movement swallows the click — toggling feels unreliable.
      // `filter` keeps drags from starting on these elements;
      // `preventOnFilter: false` lets their clicks through untouched.
      // Custom-object rows (`.object-node`) are not draggable either: they
      // live in the pane for the pages they host, and moving the object
      // itself is the job of its own Move action.
      filter: ".arrow-wrapper, .actions-wrapper, .object-node",
      preventOnFilter: false,
      // Escape (or a release outside any list) cancels the drag: the page
      // slides back to where it started and nothing is persisted. See
      // `mountSortableCancelOnEscape` for why both options are needed.
      cancelOnEscape: true,
      revertOnSpill: true,
    }));

    // vuedraggable already spliced the underlying arrays (out of the
    // source list, into the target list at the drop index), so the pane's
    // forest is visually up to date — this handler only reports the
    // intent; the pane owner records the sibling order and persists the
    // move against its own data model, re-landing the node on the
    // *persisted* parent at the next reconcile.
    const handleChange = (event: ChangeEvent) => {
      if ("added" in event) {
        // The page was dropped under a new parent (this list's owner).
        const { element, newIndex } = event.added;
        // Only pages are draggable (see the `filter` above); an object
        // node landing here would be a Sortable glitch — ignore it, the
        // next reconcile re-lands the forest on the stored pointers.
        if (element.kind !== "page") return;
        emit("movePage", element.id, props.openedPageId, newIndex);
      } else if ("moved" in event) {
        // The page was reordered among its current siblings.
        const { element, newIndex } = event.moved;
        if (element.kind !== "page") return;
        emit("reorderPage", element.id, props.openedPageId, newIndex);
      }
      // "removed" needs no handling of its own: every drop lands in some
      // list (the root list included — its `openedPageId` is null), whose
      // "added" event reports the move, source position included.
    };

    return {
      dragOptions,
      handleChange,
    };
  },
});
</script>

<style scoped lang="scss">
.nested {
  .item-container {
    display: flex;
    flex-direction: column;
    position: relative;
    row-gap: 2px;
    min-height: 2px;

    &.sortable-ghost {
      opacity: 0;
    }

    .ghost {
      opacity: 0.5;
      border-radius: $border-radius-m;
      @include themed() {
        background: t($grey-15);
      }
    }

    .dragging {
      opacity: 0.8;
      @include themed() {
        background: t($grey-15);
      }
    }

    .item-sub {
      margin-inline-start: 16px;
    }

    .item-group {
      position: relative;

      &.hidden {
        display: none;
      }
    }
  }
}
</style>
